import {
  Eye, Wrench, TrendingUp, DollarSign, Settings,
  Monitor, Users, Megaphone, ShieldCheck
} from 'lucide-react';

const errorClass = 'text-red-500 text-xs mt-1';
const labelClass = 'block text-sm font-medium text-[#00163E] mb-1.5';

const TIPOS = [
  { id: 'estrategico', title: 'Estratégico', desc: 'Visión de largo plazo, acompañamiento continuo a dirección', icon: Eye },
  { id: 'tecnico', title: 'Técnico / Especialista', desc: 'Ejecución e impacto en proyectos concretos', icon: Wrench },
];

const AREAS = [
  { id: 'estrategia', label: 'Estrategia y crecimiento', desc: 'Crecimiento\u00A0· Expansión\u00A0· Internacionalización', icon: TrendingUp },
  { id: 'finanzas', label: 'Finanzas y corporate', desc: 'Finanzas\u00A0· Tesorería\u00A0· M&A\u00A0· Inversión', icon: DollarSign },
  { id: 'operaciones', label: 'Operaciones y producción', desc: 'Operaciones\u00A0· Producción\u00A0· Procesos\u00A0· Logística', icon: Settings },
  { id: 'comercial', label: 'Comercial y marketing', desc: 'Comercial\u00A0· Marketing\u00A0· Canal\u00A0· Ventas', icon: Megaphone },
  { id: 'personas', label: 'Personas y gobierno', desc: 'RRHH\u00A0· Gobierno corporativo\u00A0· Consejos\u00A0· Sucesión', icon: Users },
  { id: 'tecnologia', label: 'Tecnología y digital', desc: 'Digitalización\u00A0· Tecnología\u00A0· IA', icon: Monitor },
  { id: 'sectorial', label: 'Sectorial y cumplimiento', desc: 'Industria\u00A0· Energía\u00A0· Construcción\u00A0· ESG', icon: ShieldCheck },
];

const SECTORES = [
  'Sector Primario', 'Industria', 'Energía', 'Construcción', 'Comercio',
  'Transporte y Logística', 'Turismo y Hostelería', 'Finanzas y Seguros',
  'Educación', 'Sanidad', 'Administración Pública',
  'Consultoría y Servicios Profesionales', 'Tecnología', 'I+D',
  'Telecomunicaciones', 'Servicios de Información',
  'Cultura y Ocio', 'Otros',
];

const MAX_AREAS = 5;
const MAX_SECTORES = 3;

export default function StepPosicionamiento({ data, errors, onChange }) {
  const toggleArray = (field, value, max) => {
    const current = data[field] || [];
    if (current.includes(value)) {
      onChange(field, current.filter((v) => v !== value));
    } else if (current.length < max) {
      onChange(field, [...current, value]);
    }
  };

  const areasCount = (data.areasEspecializacion || []).length;
  const sectoresCount = (data.sectoresExperiencia || []).length;

  return (
    <div className="space-y-6">
      <p className="text-[#8A9BB1] text-sm mb-1">
        Defina cómo quiere ser percibido dentro de la red. Esto determina qué mandatos recibirá.
      </p>

      {/* Tipo de Asesor */}
      <div>
        <label className={labelClass}>Tipo de Asesor</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-1">
          {TIPOS.map((t) => {
            const selected = data.tipoAsesor === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onChange('tipoAsesor', t.id)}
                data-testid={`adv-tipo-${t.id}`}
                className={`p-6 rounded border text-left transition-all duration-200 ${
                  selected
                    ? 'border-[#C58C40] bg-[#C58C40]/5 shadow-sm'
                    : 'border-[#E4E6EA] hover:border-[#00163E]/30'
                }`}
              >
                <div className={`w-12 h-12 rounded flex items-center justify-center mb-4 transition-colors ${
                  selected ? 'bg-[#C58C40]/15' : 'bg-[#00163E]/5'
                }`}>
                  <t.icon size={22} className={selected ? 'text-[#C58C40]' : 'text-[#00163E]/40'} strokeWidth={1.5} />
                </div>
                <h4 className={`text-base font-medium mb-1 ${selected ? 'text-[#C58C40]' : 'text-[#00163E]'}`}>{t.title}</h4>
                <p className="text-[#8A9BB1] text-sm">{t.desc}</p>
              </button>
            );
          })}
        </div>
        {errors.tipoAsesor && <p className={errorClass}>{errors.tipoAsesor}</p>}
      </div>

      {/* Áreas de especialización (máx 5) */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-medium text-[#00163E]">Áreas de especialización</label>
          <span className={`text-xs font-medium ${areasCount >= MAX_AREAS ? 'text-[#C58C40]' : 'text-[#8A9BB1]'}`}>
            {areasCount}/{MAX_AREAS}
          </span>
        </div>
        <p className="text-[#8A9BB1] text-xs mb-3">Seleccione hasta {MAX_AREAS} áreas</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 auto-rows-fr gap-3">
          {AREAS.map((area) => {
            const selected = (data.areasEspecializacion || []).includes(area.id);
            const isDisabled = !selected && areasCount >= MAX_AREAS;
            return (
              <button
                key={area.id}
                type="button"
                onClick={() => toggleArray('areasEspecializacion', area.id, MAX_AREAS)}
                disabled={isDisabled}
                data-testid={`adv-area-${area.id}`}
                className={`flex flex-col gap-1 p-3 rounded border text-left transition-all duration-200 h-full ${
                  selected
                    ? 'border-[#C58C40] bg-[#C58C40]/5'
                    : isDisabled
                    ? 'border-[#E4E6EA] opacity-40 cursor-not-allowed'
                    : 'border-[#E4E6EA] hover:border-[#00163E]/30'
                }`}
              >
                <div className="flex items-center gap-3 min-h-[32px]">
                  <area.icon size={16} className={selected ? 'text-[#C58C40]' : 'text-[#00163E]/40'} strokeWidth={1.5} />
                  <span className={`text-xs font-medium leading-snug ${selected ? 'text-[#C58C40]' : 'text-[#00163E]/60'}`}>{area.label}</span>
                </div>
                <p className="text-[#8A9BB1] text-[11px] leading-snug pl-[28px]">{area.desc}</p>
              </button>
            );
          })}
        </div>
        {errors.areasEspecializacion && <p className={errorClass}>{errors.areasEspecializacion}</p>}
      </div>

      {/* Sectores (máx 3) */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-medium text-[#00163E]">Sectores de experiencia</label>
          <span className={`text-xs font-medium ${sectoresCount >= MAX_SECTORES ? 'text-[#C58C40]' : 'text-[#8A9BB1]'}`}>
            {sectoresCount}/{MAX_SECTORES}
          </span>
        </div>
        <p className="text-[#8A9BB1] text-xs mb-3">Seleccione hasta {MAX_SECTORES} sectores</p>
        <div className="flex flex-wrap gap-2">
          {SECTORES.map((s) => {
            const selected = (data.sectoresExperiencia || []).includes(s);
            const isDisabled = !selected && sectoresCount >= MAX_SECTORES;
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleArray('sectoresExperiencia', s, MAX_SECTORES)}
                disabled={isDisabled}
                data-testid={`adv-sector-${s.replace(/\s+/g, '-').toLowerCase()}`}
                className={`px-3 py-1.5 text-xs rounded-full border transition-all duration-200 ${
                  selected
                    ? 'border-[#C58C40] bg-[#C58C40]/10 text-[#C58C40]'
                    : isDisabled
                    ? 'border-[#E4E6EA] text-[#00163E]/25 cursor-not-allowed'
                    : 'border-[#E4E6EA] text-[#00163E]/50 hover:border-[#00163E]/30'
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
        {errors.sectoresExperiencia && <p className={errorClass}>{errors.sectoresExperiencia}</p>}
      </div>

      {/* Decisión empresarial compleja (reemplaza "Impacto demostrable") */}
      <div>
        <label className={labelClass}>La decisión empresarial más compleja en la que ha participado</label>
        <p className="text-[#8A9BB1] text-xs mb-2">
          Describa el contexto, su rol y el resultado. Mínimo 20 caracteres.
        </p>
        <textarea
          value={data.decisionCompleja || ''}
          onChange={(e) => onChange('decisionCompleja', e.target.value)}
          placeholder="Describa una situación empresarial compleja donde su criterio o intervención marcó la diferencia. Incluya el contexto, qué decisión se tomó y cuál fue el resultado."
          rows={5}
          maxLength={2000}
          data-testid="adv-field-decision-compleja"
          className={`w-full px-4 py-3 bg-white border rounded text-sm text-[#00163E] placeholder:text-[#8A9BB1]/60 outline-none transition-colors duration-200 resize-none ${
            errors.decisionCompleja ? 'border-red-400 focus:border-red-500' : 'border-[#E4E6EA] focus:border-[#C58C40]'
          }`}
        />
        <div className="flex justify-between mt-1">
          {errors.decisionCompleja ? <p className={errorClass}>{errors.decisionCompleja}</p> : <span />}
          <span className={`text-xs ${(data.decisionCompleja || '').length < 20 ? 'text-[#8A9BB1]' : 'text-[#C58C40]'}`}>
            {(data.decisionCompleja || '').length}/2000 {(data.decisionCompleja || '').length < 20 && `(mín. 20)`}
          </span>
        </div>
      </div>
    </div>
  );
}
